import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const YV_API_BASE = 'https://api.youversion.com';

/**
 * OAuth callback handler for YouVersion sign-in.
 *
 * YouVersion cambió el flow en Julio 2026 (dos saltos):
 *   1. Primer callback: solo `state` (y opcionalmente `granted_permissions`).
 *      Debemos hacer navegación top-level a `${YV_API_BASE}/auth/callback?state=...`
 *      para que YouVersion emita el authorization code.
 *   2. Segundo callback: `code` + `state` → se envía al backend para
 *      intercambiar el code por access_token/refresh_token.
 *
 * Flow legacy (pre-julio 2026): venía `yvp_id` + `state` directamente.
 * Mantenemos compat enviando los params al backend cuando existan.
 */
const YouVersionOAuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state');
    const code = params.get('code');
    const yvp_id = params.get('yvp_id');
    const user_name = params.get('user_name') || '';
    const user_email = params.get('user_email') || '';
    const profile_picture = params.get('profile_picture') || '';
    const err = params.get('error');
    const err_desc = params.get('error_description');

    // Case A: YouVersion devolvió error
    if (err) {
      setStatus('error');
      setError(err_desc || err);
      return;
    }

    // Case B: data-exchange approval callback (permiso de highlights)
    const data_exchange_status = params.get('data_exchange_status');
    if (data_exchange_status) {
      if (data_exchange_status === 'granted') {
        setStatus('success');
        setProfile({ user_name: '¡Permiso concedido!' });
        setTimeout(() => navigate('/app/biblia-youversion?refresh=highlights'), 1500);
      } else {
        setStatus('error');
        const d = params.get('error_description') || params.get('error') || 'Permiso denegado';
        setError(`Permiso de highlights: ${data_exchange_status} - ${d}`);
      }
      return;
    }

    // Case C: Flow NUEVO (Julio 2026) - primer callback solo trae `state`.
    // Hay que redirigir a YouVersion para que emita el `code`.
    if (state && !code && !yvp_id) {
      // top-level navigation (NO fetch) — YouVersion lo requiere
      window.location.assign(
        `${YV_API_BASE}/auth/callback?state=${encodeURIComponent(state)}`
      );
      return;
    }

    // Case D: Segundo callback del flow nuevo — `code` + `state`
    // o Case E: Flow legacy — `yvp_id` + `state`
    if (!state || (!code && !yvp_id)) {
      setStatus('error');
      setError('Faltan parámetros del redirect (code/yvp_id o state)');
      return;
    }

    (async () => {
      try {
        const res = await axios.post(
          `${API}/youversion/auth/callback`,
          { state, code, yvp_id, user_name, user_email, profile_picture }
        );
        setProfile(res.data);
        setStatus('success');
        setTimeout(() => navigate('/app/biblia-youversion'), 1500);
      } catch (e) {
        console.error(e);
        setStatus('error');
        setError(e?.response?.data?.detail || 'No se pudo completar la conexión');
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="yv-oauth-callback">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 text-amber-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900">Conectando con YouVersion...</h2>
            <p className="text-sm text-gray-500 mt-2">No cierres esta ventana</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900">¡Conectado!</h2>
            {profile?.user_name && (
              <p className="text-sm text-gray-600 mt-2">
                Bienvenido <strong>{profile.user_name}</strong>
              </p>
            )}
            <p className="text-xs text-gray-400 mt-3">Redirigiendo...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900">Error</h2>
            <p className="text-sm text-red-600 mt-2">{error}</p>
            <button
              onClick={() => navigate('/app/biblia-youversion')}
              className="mt-6 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
              data-testid="yv-oauth-back-btn"
            >
              Volver a Biblia YouVersion
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default YouVersionOAuthCallback;
